import { ToastProvider, useCurrentUser } from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../api/document';
import {
  DocumentFormat,
  PageOrientation,
  QualioDocument,
} from '../../../api/model/document';
import {
  exportDocumentViaMedtechTask,
  exportViaPdfApi,
  QualioPdfExportCommandOptions,
} from '../../../plugins/PdfExport/QualioPdfExport';
import { currentUserHasAccessToDocument } from '../../../util/CurrentUser/User';
import { convertApiDocumentToViewableInstance } from '../../../util/DocumentApiUtils';
import { DocumentPresentationUtils } from '../../../util/DocumentPresentationUtils';
import { DocumentExportModal } from './DocumentExportModal/index';

type ExportWrapperProps = {
  qualioDocument: QualioDocument;
  isDocumentExportModalOpen: boolean;
  onClose: () => void;
  onExport?: (
    coverPageEnabled: boolean,
    controlledCopy: boolean,
    contentShrinkingEnabled: boolean,
    includeChangeControl: boolean,
    includeDocx: boolean,
  ) => void;
  activeExportMethod?: 'legacy' | 'fes';
};

export const ExportWrapper = (props: ExportWrapperProps) => {
  const currentUser = useCurrentUser();
  const { t } = useTranslation();

  const {
    useLegacyExport,
    processImagesOnExport,
    santitizeHtmlView,
    newExportService,
    usePagination,
  } = useFlags();

  const exportQualioDoc = (
    coverPageEnabled: boolean,
    controlledCopy: boolean,
    orientation: PageOrientation | undefined,
    onDocumentExportCompletion: () => void,
    onDocumentExportFailure: (msg?: string) => void,
    onAttachmentDownloadFailure: () => void,
  ) => {
    documentApi
      .fetch(props.qualioDocument.id.toString(), 1, 1)
      .then(async (documentFromApi) => {
        if (!currentUserHasAccessToDocument(currentUser, documentFromApi)) {
          onDocumentExportFailure(t('errors.exportFailedLackOfPermissions'));
          props.onClose();
          return;
        }
        const convertedDocument =
          convertApiDocumentToViewableInstance(documentFromApi);

        const documentHtml =
          await DocumentPresentationUtils.determineHtmlDocumentContentFromDocument(
            convertedDocument,
            false,
            true,
            useLegacyExport,
            santitizeHtmlView,
          );

        const options: QualioPdfExportCommandOptions = {
          qualioDocument: documentFromApi,
          coverPageEnabled,
          controlledCopy,
          orientation,
          onDocumentExportCompletion,
          onDocumentExportFailure,
          onAttachmentDownloadFailure,
          currentUser,
          documentHtml,
          processImagesOnExport,
          useLegacyExport,
          newExportService,
          enablePaginationExportStyles:
            usePagination &&
            documentFromApi.editor_version?.includes('multiroot'),
        };
        void exportViaPdfApi(options);
      })
      .catch((err) => {
        console.warn(err.toString());
        onDocumentExportFailure();
      });
  };

  const onExportDocument = (
    coverPageEnabled: boolean,
    controlledCopy: boolean,
    orientation: PageOrientation | undefined,
    contentShrinkingEnabled: boolean,
    onDocumentExportCompletion: () => void,
    onDocumentExportFailure: () => void,
    onAttachmentDownloadFailure: () => void,
  ) => {
    if (
      props.qualioDocument.document_format === DocumentFormat.DocumentUpload ||
      props.qualioDocument.editor_version === null
    ) {
      exportDocumentViaMedtechTask(
        props.qualioDocument,
        onDocumentExportCompletion,
        onDocumentExportFailure,
        coverPageEnabled,
        controlledCopy,
        contentShrinkingEnabled,
      ).catch(() => onDocumentExportFailure());
    } else {
      exportQualioDoc(
        coverPageEnabled,
        controlledCopy,
        orientation,
        onDocumentExportCompletion,
        onDocumentExportFailure,
        onAttachmentDownloadFailure,
      );
    }
  };

  return (
    <ToastProvider enableDelegation={true}>
      <DocumentExportModal
        isOpen={props.isDocumentExportModalOpen}
        onClose={props.onClose}
        document={props.qualioDocument}
        onExportDocument={onExportDocument}
        onExport={props.onExport}
        currentUser={currentUser}
        activeExportMethod={props.activeExportMethod}
      />
    </ToastProvider>
  );
};
